import { resolveCarrierLogo } from '@/utils/airlineBranding';

/**
 * Mini flight cards inside the concierge chat — the desk's search results
 * (SSE `flight_options`), in the same visual language as the Book page's
 * FlightOfferCard rows.
 *
 * Mobile rule: NOTHING may be clipped. The old single meta line packed
 * airline · stops · duration · route into one `truncate`, so on a phone the
 * duration — the thing a customer most wants before tapping — was the first
 * casualty. Metadata now wraps onto as many lines as it needs, and the price
 * column moves under the itinerary below `sm` rather than squeezing it.
 *
 * Tapping a card is the same as replying with that option — the pick is sent
 * as a normal chat message so the agent's tool flow (returns fetch / handoff)
 * stays intact.
 */

/** One wrapping row of "·"-joined facts. Renders nothing when it's all null,
 *  so a provider missing aircraft or layover data costs a line, not a gap. */
function MetaRow({ parts, className = '' }) {
  const kept = parts.filter(Boolean);
  if (!kept.length) return null;
  return (
    <p className={`text-[11.5px] font-normal leading-snug break-words ${className}`}>
      {kept.map((part, i) => (
        <span key={i}>
          {i > 0 && <span className="text-white/25 mx-1.5">·</span>}
          {part}
        </span>
      ))}
    </p>
  );
}

export default function ChatFlightOptions({ options, onPick, disabled = false }) {
  const items = options?.items || [];
  if (!items.length) return null;
  return (
    <div className="mt-2 w-full space-y-1.5">
      {options.kind === 'return' && (
        <p className="text-[11px] font-normal text-white/45 pl-1">Return flights</p>
      )}
      {items.map((item) => (
        <button
          key={item.option}
          type="button"
          disabled={disabled}
          onClick={() => onPick?.(item, options)}
          className="w-full text-left rounded-2xl border border-brand-800/30 bg-white/[0.04] backdrop-blur-md px-3 py-2.5 transition-colors hover:bg-white/[0.07] hover:border-brand-700/40 active:bg-white/[0.09] disabled:opacity-50 disabled:pointer-events-none"
        >
          <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
            <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0">
              {resolveCarrierLogo(item.carrier) ? (
                <img
                  src={resolveCarrierLogo(item.carrier)}
                  alt={`${item.airline} logo`}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-white font-medium text-sm">${(item.airline || '?').charAt(0)}</span>`;
                  }}
                />
              ) : (
                <span className="text-white font-medium text-sm">{(item.airline || '?').charAt(0)}</span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {/* Times + route. Wraps rather than truncating on a narrow phone. */}
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm text-white">
                  {item.departTime} – {item.arriveTime}
                  {item.arriveDayLabel && (
                    <sup className="ml-0.5 text-[10px] font-medium text-sky-200/90" title={`Arrives ${item.arriveDayWords || 'on another day'}`}>
                      {item.arriveDayLabel}
                      <span className="sr-only"> ({item.arriveDayWords})</span>
                    </sup>
                  )}
                </span>
                {item.originCode && item.destCode && (
                  <span className="text-[11.5px] font-normal text-white/50">
                    {item.originCode}–{item.destCode}
                  </span>
                )}
              </div>

              {/* What they're actually buying. */}
              <MetaRow
                className="text-white/60 mt-0.5"
                parts={[item.airline, item.stopsLabel, item.durationLabel, item.dateLabel]}
              />
              {/* Aircraft, flight numbers, and the connection they'd sit in. */}
              <MetaRow
                className="text-white/40 mt-0.5"
                parts={[item.aircraftLabel, item.flightNumbersLabel, item.layoverLabel]}
              />
              {/* Codeshare — why the booking may not show in that airline's app. */}
              {item.operatedByLabel && (
                <p className="text-[11.5px] font-normal leading-snug text-brand-200/70 mt-0.5 break-words">
                  {item.operatedByLabel}
                </p>
              )}

            </div>

            {/* ONE price node, not a mobile copy plus a desktop copy — a
                CSS-hidden duplicate still reaches screen readers. Below `sm`
                it wraps to its own full-width row, indented past the logo;
                from `sm` up it returns to a right-aligned column. */}
            {(item.depositLabel || item.totalLabel) && (
              <div className="w-full order-last pl-12 sm:pl-0 sm:w-auto sm:order-none sm:text-right flex-shrink-0 flex flex-wrap items-baseline gap-x-2 sm:block">
                {item.depositLabel && (
                  <span className="text-sm font-medium text-white sm:block">{item.depositLabel}</span>
                )}
                {item.totalLabel && (
                  <span className="text-[11px] font-normal text-white/60 sm:block">{item.totalLabel}</span>
                )}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}
